import dayjs from 'dayjs';
import { DataSeries } from '@/modules/calendar/model/calendar/data-series';
import { NewsEvent } from '@/modules/calendar/model/calendar/news-event';

export function getRevision(
  series: DataSeries,
  date: string,
  events: NewsEvent[],
): string | undefined {
  if (series.related) {
    const target = series.related.find(
      series => series.relation === 'HAS_PREVIOUS_REVISED_BY',
    )?.target;

    const event = events
      .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())
      .find(value => {
        return dayjs(value.date).isAfter(dayjs(date));
      });

    const entries = event?.dataSeriesEntries;

    if (entries) {
      return entries.find(e => e.dataSeriesId === target)?.actual;
    }
  }
}

// Per-symbol compression: choose at most one point per interval per symbol
export function compressPerSymbolForPriceMap(
  priceMap: Map<number, number>,
  eventMs?: number,
  symbolName?: string,
): number[] {
  const timestamps = Array.from(priceMap.keys()).sort((a, b) => a - b);
  if (!eventMs) return timestamps.slice();

  // Keep every point in an asymmetric near window: 1 minute BEFORE and 10 minutes AFTER the event
  const beforeMs = 1 * 60 * 1000; // 1 minute before
  const afterMs = 10 * 60 * 1000; // 10 minutes after
  const midMs = 60 * 60 * 1000; // 60 minutes
  const midIntervalMs = 5 * 1000; // 5 seconds (per-symbol sampling in mid zone)
  const farIntervalMs = 60 * 1000; // 60 seconds

  // Track points in each zone for logging
  const nearWindow: number[] = [];
  const midZone: number[] = [];
  const farZone: number[] = [];

  const chosenPerBucket = new Map<number, number>();
  for (const ts of timestamps) {
    const delta = ts - eventMs;
    if (delta >= -beforeMs && delta <= afterMs) {
      // keep everything in the near window (use ts itself as bucket key to preserve ordering)
      chosenPerBucket.set(ts, ts);
      nearWindow.push(ts);
      continue;
    }
    const ad = Math.abs(delta);
    const interval = ad <= midMs ? midIntervalMs : farIntervalMs;
    const bucketIndex = Math.floor((ts - eventMs) / interval);
    const bucketStart = eventMs + bucketIndex * interval;
    if (!chosenPerBucket.has(bucketStart)) {
      // choose first timestamp observed for this bucket for this symbol
      chosenPerBucket.set(bucketStart, ts);
      if (ad <= midMs) {
        midZone.push(ts);
      } else {
        farZone.push(ts);
      }
    }
  }

  const result = Array.from(chosenPerBucket.values()).sort((a, b) => a - b);

  // Log compression statistics by zone
  const nearOriginal = timestamps.filter(ts => {
    const delta = ts - eventMs;
    return delta >= -beforeMs && delta <= afterMs;
  }).length;

  const midOriginal = timestamps.filter(ts => {
    const delta = ts - eventMs;
    const ad = Math.abs(delta);
    return !(delta >= -beforeMs && delta <= afterMs) && ad <= midMs;
  }).length;

  const farOriginal = timestamps.filter(ts => {
    const delta = ts - eventMs;
    const ad = Math.abs(delta);
    return ad > midMs;
  }).length;

  const symbolPrefix = symbolName ? `[${symbolName}]` : '';
  console.debug(
    `${symbolPrefix} Near: ${nearOriginal}→${nearWindow.length} | Mid: ${midOriginal}→${midZone.length} | Far: ${farOriginal}→${farZone.length} | Total: ${timestamps.length}→${result.length}`,
  );

  return result;
}
